import type { CSSProperties, ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './drawer'
import type { DrawerSide } from './drawer'
import './app-compat-surfaces.css'

export interface AppDrawerProps {
  readonly actions?: ReactNode
  readonly anchor?: DrawerSide
  readonly children?: ReactNode
  readonly containerSx?: CSSProperties
  readonly description?: ReactNode
  readonly onClose: () => void
  readonly open: boolean
  readonly title?: ReactNode
}

export function AppDrawer({
  actions,
  anchor = 'right',
  children,
  containerSx,
  description,
  onClose,
  open,
  title,
}: AppDrawerProps) {
  const invokingElementRef = useRef<HTMLElement | null>(null)
  const previousOpenRef = useRef(false)

  useEffect(() => {
    if (open && !previousOpenRef.current && typeof document !== 'undefined') {
      const activeElement = document.activeElement
      invokingElementRef.current = activeElement instanceof HTMLElement ? activeElement : null
    }

    previousOpenRef.current = open
  }, [open])

  const restoreInvokingFocus = () => {
    const invokingElement = invokingElementRef.current
    if (!invokingElement?.isConnected) return false

    invokingElement.focus()
    return true
  }

  const handleCloseAutoFocus = (event: Event) => {
    if (!restoreInvokingFocus()) return
    event.preventDefault()
  }

  return (
    <Drawer
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          restoreInvokingFocus()
          onClose()
        }
      }}
      open={open}
    >
      <DrawerContent
        className="appCompatDrawer"
        hideClose
        onCloseAutoFocus={handleCloseAutoFocus}
        side={anchor}
        style={containerSx}
      >
        {title || description ? (
          <DrawerHeader>
            {title ? <DrawerTitle>{title}</DrawerTitle> : null}
            {description ? <DrawerDescription>{description}</DrawerDescription> : null}
          </DrawerHeader>
        ) : null}
        <div className="appCompatDrawerBody">{children}</div>
        {actions ? <DrawerFooter className="appCompatDrawerActions">{actions}</DrawerFooter> : null}
      </DrawerContent>
    </Drawer>
  )
}
